import { Stack } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { useCallback, useMemo } from 'react';
import { CopyToClipboardButton } from 'src/components';
import { SiteRelatedTabs } from 'src/components/SidePanelTabs/SiteTab/site-related-tabs';
import { SiteTab } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { SiteShareButton } from 'src/components/SiteComponents/site-share-button';
import { Address, Conversation } from 'src/graphql';
import { SectionHeader } from 'src/pages/Conversations/SidePanel/section-header';
import { useUpdateAssociatedSite } from 'src/pages/Sites/use-update-associated-site';
import useConversationViewsStore from 'src/store/conversation-views';
import { AssociatedSiteFormValues, useAssociatedSiteForm } from './use-associated-site-form';

export const ConversationSite = ({
  conversation,
  address,
  variant = 'standard',
}: {
  conversation?: Conversation;
  address?: Partial<Address>;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
}) => {
  const { associatedAddressesPlaceholders } = useConversationViewsStore();

  const currentAddress = useMemo(
    () =>
      (address ??
        conversation?.address ??
        associatedAddressesPlaceholders[conversation?.conversationId!]?.[0]) as Address,
    [
      address,
      associatedAddressesPlaceholders,
      conversation?.address,
      conversation?.conversationId,
    ],
  );

  const disabled =
    (conversation?.conversationStatusId as ConversationStatus) === ConversationStatus.Closed ||
    !!conversation?.isDeleted;

  const updateAssociatedSite = useUpdateAssociatedSite(
    currentAddress,
    conversation?.conversationId,
  );

  const { associatedSiteFormFields } = useAssociatedSiteForm(currentAddress, disabled);

  const onSubmitAssociatedSite = useCallback(
    (values: AssociatedSiteFormValues) => {
      void updateAssociatedSite(values);
    },
    [updateAssociatedSite],
  );

  return (
    <SiteTab
      address={currentAddress}
      onSubmit={onSubmitAssociatedSite}
      fields={associatedSiteFormFields}
      hideAddressRow
      propertiesHeader={
        <>
          <Stack
            data-testid="conversation-site-actions"
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            paddingBottom={0.75}
          >
            <CopyToClipboardButton content={currentAddress?.fullAddress!} />
            <SiteShareButton
              address={currentAddress}
              partnerId={currentAddress?.partner?.partnerId ?? currentAddress?.partnerId}
            />
            <SiteMenu address={currentAddress} conversation={conversation} />
          </Stack>
          <SectionHeader
            title="Properties"
            sx={{
              lineHeight: 1.2,
              marginTop: 0.25,
              padding: (theme) => theme.spacing(0.75, 0, 1),
            }}
          />
        </>
      }
    >
      <SiteRelatedTabs address={currentAddress} variant={variant} />
    </SiteTab>
  );
};
